import React, { useEffect, useRef, useState } from 'react';
import styles from './AudioRecorder.module.css';
import { MSToTimeString } from 'utils/stringUtils';
import FusionInputSlider from 'components/FusionSlider/FusionInputSlider';
import { useAudioRecorder } from 'hooks/useAudioRecorder';
import { PiMicrophoneFill } from 'react-icons/pi';
import { BsStopFill, BsUpload } from 'react-icons/bs';
import { IoRefresh } from 'react-icons/io5';
import FusionInputField from 'components/FusionInputField/FusionInputField';
import RecordNew from './sub-components/RecordNew';
import SelectedFileControl from 'components/FileSelect/sub-components/SelectedFileControl';
import { useSendRecording } from './Recorder.api';
import ClosableModal from 'components/ClosableModal/ClosableModal';
import FileOverridePrompt from 'components/FileOverridePrompt';

export const MODES = {
  none: 0,
  'record-ready': 1,
};

const DEFAULT_MAX_DURATION_MS = 2 * 1000 * 60;

export default function AudioRecorder({
  usedNames,
  fileType,
  maxDuration = DEFAULT_MAX_DURATION_MS,
  clear,
  onChange = (blob, name) => {},
  onPlay = () => {},
  onSave = () => {},
}) {
  const { uploadProgress, clearProgress, sendRecording } = useSendRecording();
  const { blob, recording, duration, toggleRecording, clearAudio } = useAudioRecorder(fileType);

  const audioRef = useRef(null);
  const timeoutRef = useRef(null);

  const [mode, setMode] = useState(MODES['none']);
  const [fileName, setFileName] = useState('');
  const [playing, setPlaying] = useState(false);
  const [errMsg, setErrMsg] = useState('');
  const [duplicateFile, setDuplicateFile] = useState(false);
  const [promptOverride, setPromptOverride] = useState(false);

  const onClear = () => {
    setMode(MODES['none']);
    clearAudio();
  };

  useEffect(() => {
    onChange(blob, fileName);
  }, [blob, fileName, onChange]);

  useEffect(() => {
    if (clear) {
      clearAudio();
      setMode(MODES['none']);
    }
  }, [clear]);

  useEffect(() => {
    if (uploadProgress !== null) setMode(MODES['record-ready']);
    else setMode(MODES['none']);
  }, [uploadProgress]);

  useEffect(() => {
    audioRef.current.addEventListener('play', onRefPlayed);
    audioRef.current.addEventListener('pause', onRefStopped);
    audioRef.current.addEventListener('ended', onRefStopped);

    return () => {
      audioRef.current?.removeEventListener('play', onRefPlayed);
      audioRef.current?.removeEventListener('pause', onRefStopped);
      audioRef.current?.removeEventListener('ended', onRefStopped);
    };
  }, []);

  const onUploadNew = async () => {
    if (!blob || !fileName) {
      setErrMsg('Missing file name');
      return;
    }

    setErrMsg('');

    try {
      const ret = await sendRecording(blob, fileName, 'mp3');

      if (ret?.data?.expireTime)
        timeoutRef.current = setTimeout(() => {
          clearAudio();
        }, ret.data.expireTime);

      setDuplicateFile(ret?.data?.duplicateFile || false);
    } catch (error) {
      console.error('Error in AudioRecorder:onUploadNew', error);
      setErrMsg(error.message);
    }
  };

  const onPlayback = () => {
    audioRef.current.src = URL.createObjectURL(blob);
    audioRef.current.play();
  };

  const onStopPlayback = () => {
    audioRef.current.pause();
    audioRef.current.src = null;
    setPlaying(false);
    URL.revokeObjectURL(blob);
  };

  const startSave = () => {
    if (!duplicateFile) {
      onSaveInt(false);
      return;
    }

    setPromptOverride(true);
  };

  const onSaveInt = (override) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    setPromptOverride(false);
    onSave(false, override);
    onClear();
  };

  const onPlayInt = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    onPlay();
    onClear();
  };

  const onRefPlayed = () => setPlaying(true);
  const onRefStopped = () => setPlaying(false);

  return (
    <div className="flex-fill d-flex">
      {mode === MODES['none'] && (
        <RecordNew
          duration={duration}
          maxDuration={maxDuration}
          fileName={fileName}
          onFileNameChange={(v) => setFileName(v)}
          onClear={onClear}
          onUpload={onUploadNew}
          toggleRecording={toggleRecording}
          recording={recording}
          onPlayback={onPlayback}
          onStopPlayback={onStopPlayback}
          playing={playing}
          errMsg={errMsg}
        />
      )}

      {mode === MODES['record-ready'] && (
        <SelectedFileControl
          file={{ name: fileName, size: blob?.size }}
          progress={uploadProgress}
          onClear={onClear}
          onPlay={onPlayInt}
          onSave={startSave}
        />
      )}

      <FileOverridePrompt
        fileName={fileName}
        open={promptOverride}
        onClose={() => setPromptOverride(false)}
        onResolve={onSaveInt}
      />

      <audio ref={audioRef} autoPlay />
    </div>
  );
}
